import { NextPage } from 'next';
import { apiPlansGetV2, planPageType } from 'api/plans/get';
import ProductsPage from '../../components/pages/products';
import { Product } from 'entities/product';
import images from 'lib/images.json';
import {
  PRODUCT_CONFIG,
  DAILY_ESSENTIALS_DUO,
} from 'components/pages/products/config/product-data';
import { getShopifyVariantResults } from 'lib/shopify/get-shopify-variants-direct';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';

interface ProductsProps {
  products: Product[];
}

// Helper function to get only the main products with images and add explicit order
const getMainProducts = (products: Product[]) => {
  const mainProductSkus: string[] = [
    PRODUCT_SKUS.DAILY_SYNBIOTIC,
    PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC,
    PRODUCT_SKUS.VAGINAL_SYNBIOTIC,
    PRODUCT_SKUS.ENERGY_FOCUS,
    PRODUCT_SKUS.SLEEP_RESTORE,
    PRODUCT_SKUS.DAILY_MULTIVITAMIN,
  ];
  return products
    .filter((product) => mainProductSkus.includes(product.code))
    .map((product) => {
      const config = getProductConfig(product.code);
      return {
        ...product,
        image: config.image || images.jar,
        order: config.order,
        isFeatured: config.isFeatured,
        showDiscountPill: config.showDiscountPill ?? false,
        discountText: config.discountText ?? 'Sale',
        imageConfig: config.imageConfig,
      };
    })
    .sort((a, b) => a.order - b.order);
};

// Helper function to get product configuration
const getProductConfig = (code: string) => {
  return (
    PRODUCT_CONFIG[code] || { order: 999, isFeatured: false, image: images.jar }
  );
};

const Products: NextPage<ProductsProps> = ({ products }) => {
  // ProductsProvider needs the full catalog (including SRP variants) so the
  // cart panel can resolve any in-cart SKU; the page itself only displays
  // the filtered subset.
  const displayProducts = [
    ...getMainProducts(products),
    {
      ...DAILY_ESSENTIALS_DUO,
      showDiscountPill: PRODUCT_CONFIG['ds-duo']?.showDiscountPill ?? false,
      discountText: PRODUCT_CONFIG['ds-duo']?.discountText ?? 'Sale',
    },
  ].sort((a, b) => a.order - b.order);

  return <ProductsPage products={displayProducts} />;
};

export async function getStaticProps() {
  const products = await apiPlansGetV2(planPageType.all);
  const props: { products: Product[]; shopifyProducts?: unknown } = {
    products,
  };

  const { results } = await getShopifyVariantResults();
  props.shopifyProducts = results;

  return {
    props,
    revalidate: 60 * 60, // 1 hour in seconds
  };
}

export default Products;
